/// <amd-module name="Build/ApprovalsPanel/LogsViewPanel" />
import * as React from "react";

import { getResourceUrl } from "Build/ApprovalsPanel/ResourceLinks";
import { ILogsViewPanelService } from "Build/ApprovalsPanel/LogsViewPanelService.types";
import * as Resources from "Build/ApprovalsPanel/Resources";
import { getRemainingMinutesForCheckTimeout, getCheckTimeoutOrReevaluationString, getCheckRunStatusIndicatorData, isInternalTaskCheck, getResourceTypeIcons } from "Build/ApprovalsPanel/Utilities";
import { TaskCheckDetails } from "Build/Client/Contracts/DataProvider";
import { CheckIssueType } from "Build/Client/Contracts/PipelinesChecks";

import { LogViewer } from "PipelinesUI/LogViewer";

import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useComponentContext } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";

import { ContentSize } from "VSSUI/Callout";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Panel } from "VSSUI/Panel"
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { css } from "VSSUI/Util"

export interface ILogsViewPanelProps {
    check: TaskCheckDetails;
    arePendingChecksPresent: boolean;
    projectId: string;
    onDismiss: () => void;
    onBackClick: (event: React.SyntheticEvent<HTMLElement>) => void;
}

export const LogsViewPanel: React.SFC<ILogsViewPanelProps> = props => {
    const context = useComponentContext();
    const themeService = context.pageContext.getService<IVssThemeService>("IVssThemeService");
    themeService.createThemedSelector(".dark-run-logs", t => (t.isDark ? t : themeService.getTheme("ms.vss-web.vsts-theme-dark")));
    const service = context.pageContext.getService<ILogsViewPanelService>("ILogsViewPanelService");
    service.initialize();

    const sections = service.fetchData(props.check, props.projectId);
    const showCheckIssues = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableChecksScalabilityPhase1BuildCheckIssues", false)

    let checkRunStatus = getCheckRunStatusIndicatorData(props.check.status);
    let description = checkRunStatus.label;

    if (props.arePendingChecksPresent && !isInternalTaskCheck(props.check)) {
        const remainingTimeoutInMinutes = getRemainingMinutesForCheckTimeout(props.check.timeout, props.check.createdDate);
        description = remainingTimeoutInMinutes > 0 ? getCheckTimeoutOrReevaluationString(Resources.Timeout, remainingTimeoutInMinutes) : checkRunStatus.label;
    }

    const checksComplianceWarning: JSX.Element =
        <div className="flex-row">
            <div className="checks-description">{description}</div>
            {showCheckIssues && !!props.check.issue && (
                <div className="checks-issue-pill">
                    <Pill
                        className={css(props.check.issue.type === CheckIssueType.Warning && "warning",
                                        props.check.issue.type === CheckIssueType.Error && "error")}
                        size={PillSize.regular}
                        variant={PillVariant.outlined}>
                        {props.check.issue.message}
                    </Pill>
                </div>
            )}
        </div>;

    const resourceIconName = props.check.resource ? getResourceTypeIcons(props.check.resource.type) : "Server";
    const resourceName = props.check.resource ? props.check.resource.name : "";

    return (
        <Panel
            className="checks-logs-view-panel themed dark-run-logs"
            backButtonProps={{ onClick: props.onBackClick }}
            size={ContentSize.Large}
            lightDismiss={true}
            onDismiss={props.onDismiss}
            description={description}
            descriptionItem={showCheckIssues && !!props.check.issue ? checksComplianceWarning: undefined}
            titleProps={{ text: props.check.taskCheckConfigSettings.displayName }} >
                <div className="flex-column flex-grow">
                    <div className="flex-column rhythm-vertical-24 v-scroll-auto flex-grow">
                        <div className="flex-column bolt-default-horizontal-spacing">
                            <span className="pipelines-checks-section-header body-m font-weight-semibold">{Resources.Resource}</span>
                            <div className="flex-row flex-center pipelines-checks-section-header-details">
                                <Icon
                                    className="pipelines-resource-icon primary-text"
                                    iconName={resourceIconName}
                                />
                                <div className="flex-column flex-grow h-scroll-hidden">
                                    <span className="body-m text-ellipsis checks-resource-name">
                                        <Link
                                            className="pipelines-resource-link"
                                            href={getResourceUrl(context.pageContext, props.check.resource, true)}
                                            target="_blank">
                                            {resourceName}
                                        </Link>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="logs">
                            <LogViewer sections={sections}
                                className="logs-view custom-scrollbar v-scroll-auto h-scroll-auto"
                                showTimestamps={true} />
                        </div>
                    </div>
                </div>
        </Panel>
    );
}
